import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Printer, ArrowLeft } from 'lucide-react';
import { formatCurrency } from '../../lib/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtDate = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return String(d); }
};

export default function SyndicationDealReport() {
  const { sid, dealId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/syndications/syndicator/${sid}/deal/${dealId}/report`,
        { headers: { ...getAuthHeaders(), 'X-Tenant': 'live' } });
      setData(res.data);
    } catch (e) { setError(e?.response?.data?.detail || 'Could not load report'); }
  }, [sid, dealId, getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  if (error) return <div className="p-8 text-red-600">{error}</div>;
  if (!data) return <div className="p-8 text-slate-500">Loading report…</div>;

  const { syndicator, deal, position, payments, fees, losses, disbursements, totals } = data;
  let running = 0;

  return (
    <div style={{ background: '#fff', minHeight: '100vh' }}>
      <style>{`@media print { .no-print { display: none !important; } body { background: #fff; } } .rpt { max-width: 880px; margin: 0 auto; padding: 24px; color: #1c1c1a; font-size: 13px; } .rpt h1 { font-size: 22px; font-weight: 600; margin: 0; } .rpt h2 { font-size: 15px; font-weight: 600; margin: 22px 0 8px; } .rpt table { width: 100%; border-collapse: collapse; } .rpt th, .rpt td { padding: 6px 8px; border-bottom: 1px solid #e3e3dc; text-align: right; } .rpt th:first-child, .rpt td:first-child { text-align: left; } .rpt th { color: #6b6b66; font-weight: 500; } .kv { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px 24px; margin-top: 10px; } .kv div span { color: #6b6b66; } .neg { color: #a32d2d; } .pos { color: #1d7a55; }`}</style>

      <div className="no-print" style={{ borderBottom: '1px solid #e3e3dc', padding: '10px 24px', display: 'flex', gap: 8 }}>
        <Button variant="outline" size="sm" onClick={() => navigate(-1)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
        <Button size="sm" onClick={() => window.print()}><Printer className="w-4 h-4 mr-1" /> Print / Save as PDF</Button>
      </div>

      <div className="rpt">
        <h1>Syndication deal report</h1>
        <div className="kv">
          <div><span>Investor: </span><b>{syndicator.name}</b></div>
          <div><span>Business / merchant: </span><b>{deal.merchant_name || deal.name}</b></div>
          <div><span>Deal: </span><b>{deal.name} (#{deal.deal_number})</b></div>
          <div><span>Syndication %: </span><b>{syndicator.ownership_percentage}%</b></div>
          <div><span>Funding: </span><b>{formatCurrency(deal.funding_amount)}</b></div>
          <div><span>Factor / Payback: </span><b>{deal.factor}x · {formatCurrency(deal.payback)}</b></div>
          <div><span>Capital deployed: </span><b>{formatCurrency(position.capital_deployed)}</b></div>
          <div><span>Their RTR: </span><b>{formatCurrency(position.rtr_target)}</b></div>
          <div><span>Status: </span><b>{deal.status}</b></div>
        </div>

        <h2>Payment history (their share)</h2>
        <table>
          <thead><tr><th>Date</th><th>Type</th><th>Gross payment</th><th>Their share</th><th>Running</th></tr></thead>
          <tbody>
            {payments.length === 0 ? <tr><td colSpan={5} style={{ textAlign: 'center', color: '#999' }}>No payments yet.</td></tr>
              : payments.map((p, i) => { running += p.their_share; return (
                <tr key={i}><td>{fmtDate(p.date)}</td><td>{p.type.toLowerCase().replace('_', ' ')}</td>
                  <td>{formatCurrency(p.gross_amount)}</td><td>{formatCurrency(p.their_share)}</td><td>{formatCurrency(running)}</td></tr>
              ); })}
          </tbody>
        </table>

        <h2>Disbursements (paid to investor)</h2>
        <table>
          <thead><tr><th>Date</th><th>Status</th><th>Reference</th><th>Amount</th></tr></thead>
          <tbody>
            {disbursements.length === 0 ? <tr><td colSpan={4} style={{ textAlign: 'center', color: '#999' }}>No disbursements yet.</td></tr>
              : disbursements.map((d, i) => (
                <tr key={i}><td>{fmtDate(d.date)}</td><td>{d.status}</td><td>{d.reference || '-'}</td><td>{formatCurrency(d.amount)}</td></tr>
              ))}
          </tbody>
        </table>

        <h2>Fees</h2>
        <table>
          <tbody>{fees.map((f, i) => (
            <tr key={i}><td>{f.label}</td><td className={f.amount < 0 ? 'neg' : ''}>{formatCurrency(f.amount)}</td></tr>
          ))}</tbody>
        </table>

        <h2>Losses</h2>
        <table>
          <tbody>{losses.map((l, i) => (
            <tr key={i}><td>{l.label}</td><td className={l.amount < 0 ? 'neg' : ''}>{formatCurrency(l.amount)}</td></tr>
          ))}</tbody>
        </table>

        <h2>Summary</h2>
        <table>
          <tbody>
            <tr><td>Total collected (their share)</td><td className="pos">{formatCurrency(totals.collected_share)}</td></tr>
            <tr><td>Origination fee collected</td><td>{formatCurrency(totals.origination_fee)}</td></tr>
            <tr><td>Fees collected</td><td>{formatCurrency(totals.fees_collected)}</td></tr>
            <tr><td>Write-off loss</td><td className="neg">-{formatCurrency(totals.write_off_loss)}</td></tr>
            <tr><td>Lawyer / legal costs</td><td className="neg">-{formatCurrency(totals.lawyer_fees)}</td></tr>
            <tr><td>Servicing charged</td><td className="neg">-{formatCurrency(totals.servicing)}</td></tr>
            <tr><td><b>Net to investor</b></td><td><b>{formatCurrency(totals.net)}</b></td></tr>
            <tr><td>Disbursed to date</td><td className="neg">-{formatCurrency(totals.disbursed)}</td></tr>
            <tr><td><b>Available balance</b></td><td><b>{formatCurrency(totals.available_balance)}</b></td></tr>
          </tbody>
        </table>

        <p style={{ color: '#999', marginTop: 24, fontSize: 11 }}>Generated {fmtDate(data.generated_at)} · Power Funding Solutions</p>
      </div>
    </div>
  );
}
